import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Mail, Send } from 'lucide-react';
import { api, extractErrorMessage } from '../../../lib/api';
import { useTenantSettings } from '../../../contexts/TenantSettingsContext';

/**
 * Onglet "Emails" — US-ADM-06.
 *
 * Configuration de l'expéditeur tenant (Resend) + reply-to + domaine
 * + statut de vérification + bouton "envoyer un email de test".
 */
export function EmailConfigTab() {
  const { settings, refresh } = useTenantSettings();
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [senderDomain, setSenderDomain] = useState('');
  const [domainStatus, setDomainStatus] = useState<
    'non_configure' | 'en_attente_validation' | 'valide'
  >('non_configure');

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Test send
  const [testTo, setTestTo] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    const e = settings.email;
    setSenderName(e.sender_name);
    setSenderEmail(e.sender_email);
    setReplyTo(e.reply_to);
    setNotificationEmail(e.notification_email);
    setSenderDomain(e.sender_domain);
    setDomainStatus(e.domain_status);
  }, [settings]);

  if (!settings) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.patch('/admin/tenant/email', {
        sender_name: senderName,
        sender_email: senderEmail,
        reply_to: replyTo,
        notification_email: notificationEmail,
        sender_domain: senderDomain,
        domain_status: domainStatus,
      });
      await refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (!testTo) return;
    setTestSending(true);
    setTestResult(null);
    setErrorMessage(null);
    try {
      const res = await api.post<{ data: { provider: string; provider_id: string | null } }>(
        '/admin/tenant/email/test',
        {
          to: testTo,
          sujet: 'Email de test — {{tenant.nom}}',
          contenu_html: `
<div style="font-family: system-ui, sans-serif; padding: 20px;">
  <h2>Bonjour 👋</h2>
  <p>Cet email confirme que la configuration Resend de <strong>{{tenant.nom}}</strong> fonctionne correctement.</p>
  <p>Envoyé le {{timestamp}} via mygmao.pro.</p>
</div>`.trim(),
          variables: {},
        },
      );
      setTestResult(
        `Email envoyé via ${res.data.data.provider}` +
          (res.data.data.provider_id ? ` (id: ${res.data.data.provider_id})` : ''),
      );
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setTestSending(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
        💡 La clé API Resend est définie au niveau plateforme (variable d'environnement{' '}
        <code className="font-mono">RESEND_API_KEY</code>) — elle n'apparaît jamais dans cette UI.
        Vous configurez uniquement le domaine et l'expéditeur de votre tenant. En dev, si Resend
        n'est pas configuré, les emails partent vers Mailhog (
        <a href="http://localhost:8025" target="_blank" rel="noreferrer" className="underline">
          http://localhost:8025
        </a>
        ).
      </div>

      <form onSubmit={onSave} className="space-y-4">
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <Mail className="w-4 h-4 mr-2 text-primary-600" />
            Expéditeur
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nom expéditeur" hint='ex. "GMAO Établissement A"'>
              <input
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Email expéditeur" hint="ex. noreply@etablissement.fr">
              <input
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Adresse de réponse (reply-to)">
              <input
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Email de notification interne" hint="Copies / alertes système">
              <input
                type="email"
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Domaine d'envoi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Domaine" hint="ex. mygmao-etablissement.fr">
              <input
                value={senderDomain}
                onChange={(e) => setSenderDomain(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Statut de vérification">
              <select
                value={domainStatus}
                onChange={(e) =>
                  setDomainStatus(
                    e.target.value as 'non_configure' | 'en_attente_validation' | 'valide',
                  )
                }
                className={inputClass}
              >
                <option value="non_configure">Non configuré</option>
                <option value="en_attente_validation">En attente de validation</option>
                <option value="valide">Validé (DKIM/SPF OK)</option>
              </select>
            </Field>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Configurez les enregistrements DNS DKIM/SPF auprès de Resend avant de passer le statut à
            "validé".
          </p>
        </section>

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          {savedAt && (
            <span className="inline-flex items-center text-sm text-green-600">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Enregistré
            </span>
          )}
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Enregistrer la configuration
          </button>
        </div>
      </form>

      {/* Test send */}
      <section className="rounded-lg border border-gray-200 p-4 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Envoi de test</h2>
        <p className="text-xs text-gray-600 mb-3">
          Envoyez un email à n'importe quelle adresse pour valider la configuration. L'envoi utilise
          les paramètres ci-dessus.
        </p>
        <div className="flex flex-wrap items-center space-x-2 space-y-2 sm:space-y-0">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="destinataire@example.com"
            className={inputClass + ' flex-1 min-w-[200px]'}
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={testSending || !testTo}
            className="inline-flex items-center px-4 py-2 border border-primary-200 text-primary-700 bg-white rounded-lg text-sm font-medium hover:bg-primary-50 disabled:opacity-50"
          >
            {testSending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Envoyer un test
          </button>
        </div>
        {testResult && (
          <p className="text-sm text-green-600 mt-2 inline-flex items-center">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {testResult}
          </p>
        )}
      </section>
    </div>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}
